import cn from 'classnames';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router';

import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import {
  DentalNotationFormat,
  FeatureFlag,
  OrganizationSettings,
} from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { OrganizationStaffRoleType } from '@/shared/api/protocol-ts/model/dto_organization_staff_pb';
import { PATHS } from '@/shared/config';
import {
  useAppDispatch,
  useAppSelector,
  useIsNonDentalOrganization,
  useMedia,
  useTab,
} from '@/shared/hooks';
import { useLanguages } from '@/shared/hooks/useLanguages';
import { getImageSrc, trackEvent, validateImageFile } from '@/shared/lib';
import {
  Button,
  Column,
  Layout,
  MobileLayout,
  Select,
  Tabs,
  WidgetCard,
  WidgetLayout,
} from '@/shared/ui/';

import { billingModel, usePurchasePrompt } from '@/entities/billing';
import { ConfirmModal, ModalID, modalModel } from '@/entities/modal';
import {
  getOrganizationPrintName,
  i18n as organizationI18n,
  organizationModel,
  useCountriesOptions,
} from '@/entities/organization';
import { templatesModel } from '@/entities/templates';
import { userModel } from '@/entities/user';

import { AnalyticsModelList } from '@/features/analyticsModelEditor';
import { BillingInformation } from '@/features/billingInformation';
import { Footer } from '@/features/footer';
import { RemoveAssetModal, useRemoveAssetModal } from '@/features/removeAsset';
import {
  DocumentTemplateList,
  TemplatesSidebar,
} from '@/features/templateList';
import { useUploadAssetContext } from '@/features/uploadAsset';
import { UploadImage } from '@/features/uploadImage';

import { AboutLabelingModal } from '@/widgets/AboutLabelingModal';
import { AddStaffModal } from '@/widgets/AddStaffModal';
import { BillingInformationModal } from '@/widgets/BillingInformationModal';
import {
  Diagnocoins,
  Packages,
  Subscriptions,
} from '@/widgets/CurrentSubscription';
import { DiagnocoinHistoryModal } from '@/widgets/DiagnocoinHistoryModal';
import { EditCompanyInfoModal } from '@/widgets/EditCompanyInfoModal';
import { GetFullAccessModal } from '@/widgets/GetFullAccessModal';
import { Header, MobileHeader } from '@/widgets/Header';
import { InviteColleague } from '@/widgets/InviteColleague';
import { PermissionsModal } from '@/widgets/PermissionsModal';
import { PurchasePrompt } from '@/widgets/PurchasePrompt';
import { StorageLimit } from '@/widgets/StorageLimit';
import { SubscriptionPlans } from '@/widgets/SubscriptionPlans';

import { useOrganizationSettingsStream } from '@/app/hooks/useOrganizationSettingsStream';

import styles from './CompanySettings.module.scss';
import {
  CompanyInfoTitles,
  CONTACTS_SORT,
  DENTAL_NOTATIONS,
  TabValue,
} from './config/constants';
import { companyInfoTitles, companySettingsTabLabels } from './config/i18n';
import { getTabValuesByDoctorRoles } from './lib';
import { AnalyticsEditorPane } from './ui/AnalyticsEditorPane';
import { OrganizationApiKeys } from './ui/OrganizationApiKeys';
import { StaffMembers } from './ui/StaffMembers/StaffMembers';
import {
  TemplatesDocumentEditor,
  TemplatesSchemaEditor,
} from './ui/TemplatesEditorPane';

type CompanySettingsProps = {
  className?: string;
};

export const CompanySettings: FC<CompanySettingsProps> = (props) => {
  const { className } = props;

  const { tabValue, handleTabChange } = useTab({
    name: 'tab',
  });

  const [stampLoading, setStampLoading] = useState(false);
  const [logoLoading, setLogoLoading] = useState(false);

  const [logoUploadError, setLogoUploadError] = useState('');
  const [stampUploadError, setStampUploadError] = useState('');

  const { isMobile, isPhone } = useMedia();
  const isNonDental = useIsNonDentalOrganization();

  const organization = useAppSelector(
    organizationModel.selectors.selectCurrentOrganization,
  );
  const user = useAppSelector(userModel.selectors.selectCurrentUser);
  const userLocale = useAppSelector(userModel.selectors.selectUserLocale);
  const { ID: billingAccountID } = useAppSelector(
    billingModel.selectors.selectAccount,
  );

  const currentTeamMember = useAppSelector(
    organizationModel.selectors.selectUserByID(user.ID),
  );

  const isShopAvailable = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Show_BillingShop,
    ),
  );

  const {
    shouldShow: shouldShowPurchasePrompt,
    isOwner,
    isCISCluster,
    openShop,
  } = usePurchasePrompt();

  useOrganizationSettingsStream();

  const countryOptions = useCountriesOptions(userLocale);

  const currentCountry = useMemo(
    () =>
      countryOptions?.find(
        (countryOption) =>
          countryOption.value === organization?.Contacts?.Country,
      )?.label ?? '',
    [countryOptions, organization],
  );
  const dispatch = useAppDispatch();

  const { formatMessage } = useIntl();

  const isConsultationTemplatesEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_ConsultationTemplates,
    ),
  );
  const isAnalyticsEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_Analytics,
    ),
  );

  // Keep a directly opened tab reachable even if its feature flag is off (deep links).
  const showTemplatesTab =
    isConsultationTemplatesEnabled || tabValue === TabValue.Templates;
  const showAnalyticsTab =
    isAnalyticsEnabled || tabValue === TabValue.Analytics;

  const organizationLogo = getImageSrc(
    organization?.LogoAssetID ?? '',
    'preview',
  );
  const organizationStamp = getImageSrc(
    organization?.StampAssetID ?? '',
    'preview',
  );

  const canEditStaff = organization?.YourPermissions?.CanEditStaff ?? false;

  const canViewApiKeys = organization?.YourPermissions?.APIKeyCanView ?? false;
  const canCreateApiKeys =
    organization?.YourPermissions?.APIKeyCanCreate ?? false;
  const canDeleteApiKeys =
    organization?.YourPermissions?.APIKeyCanDelete ?? false;

  const tabList = [
    ...getTabValuesByDoctorRoles({
      role: currentTeamMember?.Role,
      canEditStaff,
      isShopAvailable,
      showTemplates: showTemplatesTab,
      showAnalytics: showAnalyticsTab,
    }),
    ...(canViewApiKeys ? [TabValue.APIKeys] : []),
  ];

  // A tab the user has no access to may still sit in the URL (bookmarks, shared links).
  const activeTab =
    tabValue && tabList.some((tab) => tab === tabValue)
      ? tabValue
      : TabValue.CompanyInfo;

  const { startUploadAsset: startUploadImage } = useUploadAssetContext();

  const {
    isRemoveAssetModalOpen,
    openRemoveAssetModal,
    closeRemoveAssetModal,
    currentAssetID,
  } = useRemoveAssetModal();

  const handleOpenModal = useCallback(() => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.EditCompanyInfoModal,
      }),
    );
  }, [dispatch]);

  const handleChange = async (image: FileWithPath, assetType: AssetType) => {
    if (assetType === AssetType.AssetType_Organization_Stamp) {
      const isImageInvalid = validateImageFile(
        image,
        (message) => setStampUploadError(message),
        formatMessage,
      );

      if (isImageInvalid) return;

      setStampLoading(true);
    } else {
      const isImageInvalid = validateImageFile(
        image,
        (message) => setLogoUploadError(message),
        formatMessage,
      );

      if (isImageInvalid) return;

      setLogoLoading(true);
    }

    try {
      if (image) {
        await startUploadImage(
          {
            files: [image],
            userID: user?.ID,
            assetType,
            meta: {
              studyType: assetType,
              patientName: user?.PersonalData?.FirstName,
            },
          },
          true,
        );
      }
    } catch (error) {
      const errorMessage = formatMessage({
        id: 'uploadImage.serverError',
        defaultMessage:
          'Server error: Image upload failed due to server error. Please retry later',
      });

      if (assetType === AssetType.AssetType_Organization_Stamp) {
        setStampUploadError(errorMessage);
      } else {
        setLogoUploadError(errorMessage);
      }
    } finally {
      setLogoLoading(false);
      setStampLoading(false);
    }
  };

  const handleChangeNotationFormat = async (
    newFormat: DentalNotationFormat,
  ) => {
    if (organization?.ID) {
      await dispatch(
        organizationModel.thunks.setOrganizationSettings({
          OrganizationID: organization.ID,
          Settings: {
            ...organization.Settings,
            DentalNotationFormat: newFormat,
          } as OrganizationSettings,
        }),
      );
    }
  };

  const handleChangeOrganizationLanguage = async (newLanguage: string) => {
    if (organization?.ID) {
      await dispatch(
        organizationModel.thunks.setOrganizationLanguage({
          OrganizationID: organization.ID,
          Language: newLanguage,
        }),
      );
    }
  };

  const handleOpenAddStaffModal = useCallback(() => {
    dispatch(
      modalModel.actions.openModal({
        modalID: ModalID.AddStaffModal,
        data: { actionType: 'Add', editingUserID: user?.ID },
      }),
    );
  }, [dispatch, user?.ID]);

  const contacts = Object.keys(organization?.Contacts || {}).sort(
    (a, b) => CONTACTS_SORT.indexOf(a) - CONTACTS_SORT.indexOf(b),
  ) as CompanyInfoTitles[];

  const hasContacts = !!contacts.length;

  const notationFormat =
    organization?.Settings?.DentalNotationFormat ||
    DentalNotationFormat.DentalNotationFormatISO;

  const { languageOptions } = useLanguages();
  const organizationLanguage = useAppSelector(
    organizationModel.selectors.selectOrganizationLanguage,
  );

  const handleDelete = async (assetID: string) => {
    openRemoveAssetModal(assetID);
    setStampUploadError('');
    setLogoUploadError('');
  };

  useEffect(() => {
    if (tabValue) {
      trackEvent({
        eventName: 'CompanySettings Page Viewed',
        properties: { tab: tabValue },
        isPageViewEvent: true,
      });
    }
  }, [tabValue]);

  useEffect(() => {
    if (
      !showTemplatesTab ||
      tabValue !== TabValue.Templates ||
      !organization?.ID
    ) {
      return;
    }

    dispatch(
      templatesModel.fetchDocumentTemplates({
        organization_id: organization.ID,
      }),
    );
    dispatch(
      templatesModel.fetchDeletedDocumentTemplates({
        organization_id: organization.ID,
      }),
    );
    dispatch(
      templatesModel.fetchEncounterSchemas({
        organization_id: organization.ID,
        include_draft: true,
      }),
    );
  }, [dispatch, showTemplatesTab, organization?.ID, tabValue]);

  useEffect(() => {
    const showBilling =
      billingAccountID &&
      isShopAvailable &&
      currentTeamMember?.Role ===
        OrganizationStaffRoleType.OrganizationStaffRoleType_FullAccess;

    const getAvailableProducts = async () => {
      await dispatch(
        billingModel.thunks.getAvailableProducts({
          Condition: { case: 'ByAccountID', value: billingAccountID },
        }),
      ).unwrap();
    };

    if (showBilling) {
      getAvailableProducts();
    }
  }, [billingAccountID, currentTeamMember, dispatch, isShopAvailable]);

  const [templatesSearchParams] = useSearchParams();
  const templatesEditor = templatesSearchParams.get('editor');
  const analyticsMode = templatesSearchParams.get('mode');

  const templatesTabContent = (() => {
    if (templatesEditor === 'schema') {
      return (
        <div className={styles.templatesWorkspace}>
          <TemplatesSchemaEditor className={styles.templatesEditor} />
        </div>
      );
    }

    if (templatesEditor === 'document') {
      return (
        <div
          className={cn(
            styles.templatesWorkspace,
            styles.templatesEditorWorkspace,
          )}
        >
          <TemplatesDocumentEditor
            className={cn(
              styles.templatesEditor,
              styles.templatesDocumentEditor,
            )}
          />
        </div>
      );
    }

    return (
      <div className={styles.templatesWorkspace}>
        <TemplatesSidebar
          className={styles.templatesSidebar}
          pathname={PATHS.companySettings}
        />
        <div className={styles.templatesContent}>
          <DocumentTemplateList />
        </div>
      </div>
    );
  })();

  const analyticsTabContent = (() => {
    if (analyticsMode === 'new' || analyticsMode === 'edit') {
      return (
        <div
          className={cn(
            styles.templatesWorkspace,
            styles.templatesEditorWorkspace,
          )}
        >
          <AnalyticsEditorPane
            className={cn(
              styles.templatesEditor,
              styles.templatesDocumentEditor,
            )}
          />
        </div>
      );
    }

    return (
      <div className={styles.templatesWorkspace}>
        <div className={styles.templatesEditor}>
          <AnalyticsModelList />
        </div>
      </div>
    );
  })();

  if (isMobile) {
    return (
      <MobileLayout>
        <MobileHeader showBackButton />

        <MobileLayout.Main>
          <div className={cn(styles.container, className)}>
            <h1 className="h3">{organization?.Name}</h1>
          </div>

          <Tabs.Root
            className={styles.columnsContainer}
            value={activeTab}
            onValueChange={handleTabChange}
          >
            <Tabs.List>
              {tabList.map((tabItem) => (
                <Tabs.Trigger
                  key={tabItem}
                  className={styles.tabs}
                  value={tabItem}
                >
                  {formatMessage(companySettingsTabLabels[tabItem])}
                </Tabs.Trigger>
              ))}

              {canEditStaff && activeTab === TabValue.Staff && (
                <Button
                  variant="primary"
                  onClick={handleOpenAddStaffModal}
                  icon="plus"
                  size={isMobile ? 'medium' : 'large'}
                  className={styles.addStaffButton}
                >
                  <FormattedMessage
                    id="companySettings.addTeamMember"
                    defaultMessage="Add team member"
                  />
                </Button>
              )}
            </Tabs.List>

            <Tabs.Content value={TabValue.CompanyInfo}>
              <WidgetLayout className={styles.companyInfoColumns}>
                <Column className={styles.column}>
                  <WidgetCard className={styles.widgetCard}>
                    <div className={styles.accountInfo}>
                      <div className={styles.organizationDescription}>
                        <h4 className={isPhone ? 'h5' : 'h4'}>
                          {organization?.Name}
                        </h4>

                        <p className={cn(styles.subText, 'p2')}>
                          <FormattedMessage
                            id="companySettings.clinicDescription"
                            defaultMessage="Clinic information will be a part of patient reports"
                          />
                        </p>
                      </div>

                      <Button
                        onClick={handleOpenModal}
                        size="medium"
                        disabled={!organization?.YourPermissions?.CanEditInfo}
                        variant="tertiary"
                      >
                        <FormattedMessage
                          id="global.edit"
                          defaultMessage="Edit"
                        />
                      </Button>
                    </div>

                    <div className={styles.companyInfoItems}>
                      <div className={styles.companyInfoItem}>
                        <p className={cn(styles.infoItemTitle, 'p2')}>
                          {formatMessage(
                            organizationI18n.printNameMessages.label,
                          )}
                        </p>

                        <p className={cn(styles.value, 'p2')}>
                          {getOrganizationPrintName(organization)}
                        </p>
                      </div>

                      {hasContacts &&
                        contacts.map((item: CompanyInfoTitles) => (
                          <div key={item} className={styles.companyInfoItem}>
                            <p className={cn(styles.infoItemTitle, 'p2')}>
                              {formatMessage(companyInfoTitles[item])}
                            </p>

                            <p className={cn(styles.value, 'p2')}>
                              {item === CompanyInfoTitles.Country
                                ? currentCountry
                                : organization?.Contacts &&
                                  organization?.Contacts[item]}
                            </p>
                          </div>
                        ))}
                    </div>
                  </WidgetCard>

                  <InviteColleague />
                </Column>

                <Column className={styles.column}>
                  <WidgetCard className={styles.clinicImages}>
                    <h4 className={isPhone ? 'h5' : 'h4'}>
                      <FormattedMessage
                        id="companySettings.clinicImages"
                        defaultMessage="Clinic images"
                      />
                    </h4>

                    <div className={styles.stampAndSignature}>
                      <UploadImage
                        className={styles.uploadImage}
                        previewImage={
                          organization?.LogoAssetID && organizationLogo
                        }
                        loading={logoLoading}
                        imageAssetID={organization?.LogoAssetID}
                        onChange={(image) =>
                          handleChange(
                            image,
                            AssetType.AssetType_Organization_Logo,
                          )
                        }
                        error={logoUploadError}
                        onRemove={handleDelete}
                        title={
                          <FormattedMessage
                            id="companySettings.logo"
                            defaultMessage="Logo"
                          />
                        }
                        dropDescription={
                          <FormattedMessage
                            id="global.dragDropPhotos"
                            defaultMessage="Drag image or add from your computer"
                          />
                        }
                      />

                      <UploadImage
                        className={styles.uploadImage}
                        previewImage={
                          organization?.StampAssetID && organizationStamp
                        }
                        loading={stampLoading}
                        onChange={(image) =>
                          handleChange(
                            image,
                            AssetType.AssetType_Organization_Stamp,
                          )
                        }
                        error={stampUploadError}
                        imageAssetID={organization?.StampAssetID}
                        onRemove={handleDelete}
                        title={
                          <FormattedMessage
                            id="companySettings.stamp"
                            defaultMessage="Stamp"
                          />
                        }
                        dropDescription={
                          <FormattedMessage
                            id="global.dragDropPhotos"
                            defaultMessage="Drag image or add from your computer"
                          />
                        }
                      />
                    </div>
                  </WidgetCard>

                  <WidgetCard className={styles.widgetCard}>
                    <h4 className={isPhone ? 'h5' : 'h4'}>
                      {formatMessage({
                        id: 'settings.dataSettings',
                        defaultMessage: 'Data settings',
                      })}
                    </h4>

                    {!isNonDental && (
                      <div className={styles.dentalSettings}>
                        <p className="p2">
                          {formatMessage({
                            id: 'settings.dentalNotationFormat',
                            defaultMessage: 'Dental notation format',
                          })}
                        </p>

                        <Select
                          options={DENTAL_NOTATIONS}
                          value={notationFormat}
                          onChange={(value) =>
                            handleChangeNotationFormat(
                              value as DentalNotationFormat,
                            )
                          }
                        />
                      </div>
                    )}

                    <div className={styles.dentalSettings}>
                      <p className="p2">
                        {formatMessage({
                          id: 'settings.organizationLanguage',
                          defaultMessage: 'Language',
                        })}
                      </p>

                      <Select
                        className={styles.capitalize}
                        options={languageOptions}
                        value={organizationLanguage}
                        placeholder={formatMessage({
                          id: 'settings.organizationLanguagePlaceholder',
                          defaultMessage: 'Not set',
                        })}
                        onChange={(value) =>
                          handleChangeOrganizationLanguage(value as string)
                        }
                      />
                    </div>
                  </WidgetCard>
                </Column>
              </WidgetLayout>
            </Tabs.Content>

            {canEditStaff && (
              <Tabs.Content
                value={TabValue.Staff}
                className={styles.tabContent}
              >
                <StaffMembers />
              </Tabs.Content>
            )}

            {showTemplatesTab && (
              <Tabs.Content
                value={TabValue.Templates}
                className={styles.templatesTab}
              >
                {templatesTabContent}
              </Tabs.Content>
            )}

            {showAnalyticsTab && (
              <Tabs.Content
                value={TabValue.Analytics}
                className={styles.templatesTab}
              >
                {analyticsTabContent}
              </Tabs.Content>
            )}

            <Tabs.Content
              value={TabValue.BillingInfo}
              className={cn(styles.tabContent, styles.billing)}
            >
              <WidgetLayout>
                <Column className={styles.billingColumn}>
                  {shouldShowPurchasePrompt ? (
                    <PurchasePrompt
                      title={formatMessage({
                        id: 'subscription.currentSubscription.header',
                        defaultMessage: 'Current Subscription',
                      })}
                      isOwner={isOwner}
                      onOpenShop={openShop}
                    />
                  ) : (
                    <Subscriptions
                      isCISCluster={isCISCluster}
                      openShop={openShop}
                    />
                  )}

                  <Diagnocoins />

                  {!shouldShowPurchasePrompt && <Packages />}

                  <StorageLimit />
                </Column>

                <Column className={styles.billingColumn}>
                  <BillingInformation />
                </Column>
              </WidgetLayout>
            </Tabs.Content>

            {isShopAvailable && (
              <Tabs.Content
                value={TabValue.SubscriptionPlans}
                className={cn(styles.tabContent, styles.subscriptionPlans)}
              >
                <WidgetLayout>
                  <SubscriptionPlans />
                </WidgetLayout>
              </Tabs.Content>
            )}

            {canViewApiKeys && (
              <Tabs.Content
                value={TabValue.APIKeys}
                className={styles.tabContent}
              >
                <OrganizationApiKeys
                  organizationID={organization?.ID ?? ''}
                  canCreate={canCreateApiKeys}
                  canDelete={canDeleteApiKeys}
                />
              </Tabs.Content>
            )}
          </Tabs.Root>

          <AddStaffModal />

          <EditCompanyInfoModal
            organizationID={organization?.ID ?? ''}
            currentCountry={currentCountry}
          />

          <PermissionsModal />

          <GetFullAccessModal />

          <BillingInformationModal />

          <DiagnocoinHistoryModal />

          <AboutLabelingModal />

          <RemoveAssetModal
            assetID={currentAssetID}
            isOpen={isRemoveAssetModalOpen}
            onCancel={closeRemoveAssetModal}
          />

          <ConfirmModal />
        </MobileLayout.Main>
      </MobileLayout>
    );
  }

  return (
    <Layout>
      <Layout.Header>
        <Header back />
      </Layout.Header>

      <Layout.Content data-section="company-settings">
        <Layout.Main>
          <div className={cn(styles.container, className)}>
            <h1 className="h3">{organization?.Name}</h1>
          </div>

          <Tabs.Root
            className={styles.columnsContainer}
            value={activeTab}
            onValueChange={handleTabChange}
          >
            <Tabs.List>
              {tabList.map((tabItem) => (
                <Tabs.Trigger
                  key={tabItem}
                  className={styles.tabs}
                  value={tabItem}
                >
                  {formatMessage(companySettingsTabLabels[tabItem])}
                </Tabs.Trigger>
              ))}

              {canEditStaff && activeTab === TabValue.Staff && (
                <Button
                  variant="primary"
                  onClick={handleOpenAddStaffModal}
                  icon="plus"
                  size={isMobile ? 'medium' : 'large'}
                  className={styles.addStaffButton}
                >
                  <FormattedMessage
                    id="companySettings.addTeamMember"
                    defaultMessage="Add team member"
                  />
                </Button>
              )}
            </Tabs.List>

            <Tabs.Content value={TabValue.CompanyInfo}>
              <WidgetLayout className={styles.companyInfoColumns}>
                <Column className={styles.column}>
                  <WidgetCard className={styles.widgetCard}>
                    <div className={styles.accountInfo}>
                      <div className={styles.organizationDescription}>
                        <h4 className={isPhone ? 'h5' : 'h4'}>
                          {organization?.Name}
                        </h4>

                        <p className={cn(styles.subText, 'p2')}>
                          <FormattedMessage
                            id="companySettings.clinicDescription"
                            defaultMessage="Clinic information will be a part of patient reports"
                          />
                        </p>
                      </div>

                      <Button
                        onClick={handleOpenModal}
                        size="medium"
                        disabled={!organization?.YourPermissions?.CanEditInfo}
                        variant="tertiary"
                      >
                        <FormattedMessage
                          id="global.edit"
                          defaultMessage="Edit"
                        />
                      </Button>
                    </div>

                    <div className={styles.companyInfoItems}>
                      <div className={styles.companyInfoItem}>
                        <p className={cn(styles.infoItemTitle, 'p2')}>
                          {formatMessage(
                            organizationI18n.printNameMessages.label,
                          )}
                        </p>

                        <p className={cn(styles.value, 'p2')}>
                          {getOrganizationPrintName(organization)}
                        </p>
                      </div>

                      {hasContacts &&
                        contacts.map((item: CompanyInfoTitles) => (
                          <div key={item} className={styles.companyInfoItem}>
                            <p className={cn(styles.infoItemTitle, 'p2')}>
                              {formatMessage(companyInfoTitles[item])}
                            </p>

                            <p className={cn(styles.value, 'p2')}>
                              {item === CompanyInfoTitles.Country
                                ? currentCountry
                                : organization?.Contacts &&
                                  organization?.Contacts[item]}
                            </p>
                          </div>
                        ))}
                    </div>
                  </WidgetCard>

                  <InviteColleague />
                </Column>

                <Column className={styles.column}>
                  <WidgetCard className={styles.clinicImages}>
                    <h4 className={isPhone ? 'h5' : 'h4'}>
                      <FormattedMessage
                        id="companySettings.clinicImages"
                        defaultMessage="Clinic images"
                      />
                    </h4>

                    <div className={styles.stampAndSignature}>
                      <UploadImage
                        className={styles.uploadImage}
                        previewImage={
                          organization?.LogoAssetID && organizationLogo
                        }
                        loading={logoLoading}
                        imageAssetID={organization?.LogoAssetID}
                        onChange={(image) =>
                          handleChange(
                            image,
                            AssetType.AssetType_Organization_Logo,
                          )
                        }
                        error={logoUploadError}
                        onRemove={handleDelete}
                        title={
                          <FormattedMessage
                            id="companySettings.logo"
                            defaultMessage="Logo"
                          />
                        }
                        dropDescription={
                          <FormattedMessage
                            id="global.dragDropPhotos"
                            defaultMessage="Drag image or add from your computer"
                          />
                        }
                      />

                      <UploadImage
                        className={styles.uploadImage}
                        previewImage={
                          organization?.StampAssetID && organizationStamp
                        }
                        loading={stampLoading}
                        onChange={(image) =>
                          handleChange(
                            image,
                            AssetType.AssetType_Organization_Stamp,
                          )
                        }
                        error={stampUploadError}
                        imageAssetID={organization?.StampAssetID}
                        onRemove={handleDelete}
                        title={
                          <FormattedMessage
                            id="companySettings.stamp"
                            defaultMessage="Stamp"
                          />
                        }
                        dropDescription={
                          <FormattedMessage
                            id="global.dragDropPhotos"
                            defaultMessage="Drag image or add from your computer"
                          />
                        }
                      />
                    </div>
                  </WidgetCard>

                  <WidgetCard className={styles.widgetCard}>
                    <h4 className={isPhone ? 'h5' : 'h4'}>
                      {formatMessage({
                        id: 'settings.dataSettings',
                        defaultMessage: 'Data settings',
                      })}
                    </h4>

                    {!isNonDental && (
                      <div className={styles.dentalSettings}>
                        <p className="p2">
                          {formatMessage({
                            id: 'settings.dentalNotationFormat',
                            defaultMessage: 'Dental notation format',
                          })}
                        </p>

                        <Select
                          options={DENTAL_NOTATIONS}
                          value={notationFormat}
                          onChange={(value) =>
                            handleChangeNotationFormat(
                              value as DentalNotationFormat,
                            )
                          }
                        />
                      </div>
                    )}

                    <div className={styles.dentalSettings}>
                      <p className="p2">
                        {formatMessage({
                          id: 'settings.organizationLanguage',
                          defaultMessage: 'Language',
                        })}
                      </p>

                      <Select
                        className={styles.capitalize}
                        options={languageOptions}
                        value={organizationLanguage}
                        placeholder={formatMessage({
                          id: 'settings.organizationLanguagePlaceholder',
                          defaultMessage: 'Not set',
                        })}
                        onChange={(value) =>
                          handleChangeOrganizationLanguage(value as string)
                        }
                      />
                    </div>
                  </WidgetCard>
                </Column>
              </WidgetLayout>
            </Tabs.Content>

            {canEditStaff && (
              <Tabs.Content
                value={TabValue.Staff}
                className={styles.tabContent}
              >
                <StaffMembers />
              </Tabs.Content>
            )}

            {showTemplatesTab && (
              <Tabs.Content
                value={TabValue.Templates}
                className={styles.templatesTab}
              >
                {templatesTabContent}
              </Tabs.Content>
            )}

            {showAnalyticsTab && (
              <Tabs.Content
                value={TabValue.Analytics}
                className={styles.templatesTab}
              >
                {analyticsTabContent}
              </Tabs.Content>
            )}

            <Tabs.Content
              value={TabValue.BillingInfo}
              className={cn(styles.tabContent, styles.billing)}
            >
              <WidgetLayout>
                <Column className={styles.billingColumn}>
                  {shouldShowPurchasePrompt ? (
                    <PurchasePrompt
                      title={formatMessage({
                        id: 'subscription.currentSubscription.header',
                        defaultMessage: 'Current Subscription',
                      })}
                      isOwner={isOwner}
                      onOpenShop={openShop}
                    />
                  ) : (
                    <Subscriptions
                      isCISCluster={isCISCluster}
                      openShop={openShop}
                    />
                  )}

                  <Diagnocoins />

                  {!shouldShowPurchasePrompt && <Packages />}

                  <StorageLimit />
                </Column>

                <Column className={styles.billingColumn}>
                  <BillingInformation />
                </Column>
              </WidgetLayout>
            </Tabs.Content>

            {isShopAvailable && (
              <Tabs.Content
                value={TabValue.SubscriptionPlans}
                className={cn(styles.tabContent, styles.subscriptionPlans)}
              >
                <WidgetLayout>
                  <SubscriptionPlans />
                </WidgetLayout>
              </Tabs.Content>
            )}

            {canViewApiKeys && (
              <Tabs.Content
                value={TabValue.APIKeys}
                className={styles.tabContent}
              >
                <OrganizationApiKeys
                  organizationID={organization?.ID ?? ''}
                  canCreate={canCreateApiKeys}
                  canDelete={canDeleteApiKeys}
                />
              </Tabs.Content>
            )}
          </Tabs.Root>

          <AddStaffModal />

          <EditCompanyInfoModal
            organizationID={organization?.ID ?? ''}
            currentCountry={currentCountry}
          />

          <PermissionsModal />

          <GetFullAccessModal />

          <BillingInformationModal />

          <DiagnocoinHistoryModal />

          <AboutLabelingModal />

          <RemoveAssetModal
            assetID={currentAssetID}
            isOpen={isRemoveAssetModalOpen}
            onCancel={closeRemoveAssetModal}
          />

          <ConfirmModal />
        </Layout.Main>
      </Layout.Content>

      <Layout.Footer>
        <Footer />
      </Layout.Footer>
    </Layout>
  );
};
